/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import './LoadSummary.scss';
import LoadSummaryItem from './LoadSummaryItem';
import {
    GetLoadSummary,
    GetLoadSummary_getLoadSummary_loadSummaries as LoadSummaryDetails,
    UpdateActiveForJobSummary as ActiveStatus,
} from '../../../types/schemaTypes';
import { useQuery, useMutation } from '@apollo/client/react';
import { GET_LOAD_SUMMARY } from '../../../graphql/client/queries';
import { UPDATE_ACTIVE_FOR_JOB_SUMMARY } from '../../../graphql/client/mutations';
import {NumericFormat as NumberFormat} from 'react-number-format';
import { Loading } from '../../_common';
import { useAuthContext } from '../../Auth/AuthProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';
import ErrorActiveModal from './ErrorActiveModal/ErrorActiveModal';
import { useLazyQuery } from '@apollo/client/react';

interface LoadSummaryProps {
    jobId: number;
    jobName: string | null;
    customerId: number;
    region?: {
        regionName: string | null;
        id: number | null;
        extSystemName: string | null;
        extSystemReference: string | null;
        regionCode: string | null;
    } | null;
    isJobCompleted: boolean;
}

const LoadSummary = ({ region, jobId, jobName, customerId, isJobCompleted }: LoadSummaryProps): React.ReactElement => {
    const { data, loading } = useQuery<GetLoadSummary>(GET_LOAD_SUMMARY, {
        variables: { jobId: jobId },
        fetchPolicy: 'network-only',
    });
    const [selectedTab, setSelectedTab] = useState('Loads');
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const [jobSummaryId, SetJobSummaryId] = useState(0);
    const [activeStatus, SetActiveStatus] = useState(false);

    const isDisplayLoadsEnabled = useAuthContext().userHasPermission(PermissionEnum.ViewLoads);
    const isAddLoadsEnabled = useAuthContext().userHasPermission(PermissionEnum.AddLoads);

    const onCompletedActiveStatus = (data: ActiveStatus): void => {
        if (data.updateActiveForJobSummary?.message === '') {
            setSuccessMsg('Load summary record updated successfully.');
            setShowSuccessModal(true);
        } else {
            setSuccessMsg(data.updateActiveForJobSummary?.message ?? '');
            setShowSuccessModal(true);
            SetActiveStatus(true);
        }
    };
    const [updateActiveForJobSummary, { loading: activeLoading }] = useMutation<ActiveStatus>(
        UPDATE_ACTIVE_FOR_JOB_SUMMARY,
        {
            onCompleted: onCompletedActiveStatus,
            refetchQueries: [
                {
                    query: GET_LOAD_SUMMARY,
                    variables: {
                        jobId: jobId,
                    },
                },
            ],
        },
    );

    const handleOnCloseErrorModal = () => {
        setShowErrorModal(false);
    };
    const handleOnCloseSuccessModal = () => {
        setShowSuccessModal(false);
    };
    const handleOnChange = async (jobId: number, active: boolean) => {
        setErrorMsg('');
        SetJobSummaryId(jobId);
        SetActiveStatus(active);

        await updateActiveForJobSummary({
            variables: {
                input: {
                    jobSummaryId: jobId,
                    isActive: active,
                },
            },
        });
    };

    if (loading) {
        return <Loading />;
    }

    const loadSummaryComponents =
        data &&
        data.getLoadSummary &&
        data.getLoadSummary.map((proppant, index: number) => {
            let totalCreated = 0;
            let totalInProgress = 0;
            let totalDelivered = 0;
            let totalRemaining = 0;
            let totalCreatedVolume = 0;
            let totalInProgressVolume = 0;
            let totalDeliveredVolume = 0;
            let totalRemainingVolume = 0;

            return proppant !== null && proppant.loadSummaries ? (
                <div key={index}>
                    {proppant.loadSummaries.map((item: LoadSummaryDetails | null, index: number):
                        | React.ReactElement
                        | undefined => {
                        if (item !== null && item.lofaName !== '') {
                            totalCreated += item.loadsCreated || 0;
                            totalInProgress += item.loadsInProgress || 0;
                            totalDelivered += item.loadsCompleted || 0;
                            totalRemaining += item.loadsRemaining || 0;
                            totalCreatedVolume += item.loadsCreatedVolume || 0;
                            totalInProgressVolume += item.loadsInProgressVolume || 0;
                            totalDeliveredVolume += item.loadsCompletedVolume || 0;
                            totalRemainingVolume += item.loadsRemainingVolume || 0;

                            if (item.jobSummaryId == jobSummaryId) {
                                const itemMirror = {...item}
                                itemMirror.isActive = activeStatus;
                                item = {...itemMirror}
                            }

                            return (
                                <LoadSummaryItem
                                    item={item}
                                    key={index}
                                    index={index}
                                    proppantName={proppant.proppantName}
                                    jobId={jobId}
                                    jobName={jobName}
                                    customerId={customerId}
                                    region={region}
                                    selectedTab={selectedTab}
                                    isJobCompleted={isJobCompleted}
                                    isAddLoadsEnabled={isAddLoadsEnabled}
                                    onChange={handleOnChange}
                                    errorMsg={errorMsg}
                                    successMsg={successMsg}
                                />
                            );
                        }
                        return undefined;
                    })}
                    {totalCreated == 0 && totalInProgress == 0 && totalDelivered == 0 && totalRemaining == 0 ? (
                        <div></div>
                    ) : (
                        <div className="Row LoadSummary-Detail-Row totals-row">
                            <div className="LoadSummary-Detail-Item totals-item justify-content-start">
                                {''}
                            </div>
                            <div className="LoadSummary-Detail-Item totals-item justify-content-start"></div>
                            <div className="LoadSummary-Detail-Item totals-item justify-content-start">
                                {''}
                            </div>
                            <div className="LoadSummary-Detail-Item totals-item justify-content-start"></div>
                            <div className="LoadSummary-Detail-Item totals-item justify-content-start"></div>
                            <div className="LoadSummary-Detail-Item totals-item justify-content-end">
                                Totals
                            </div>
                            <div className="LoadSummary-Detail-Item totals-item justify-content-start">
                                <NumberFormat
                                    value={selectedTab === 'Loads' ? totalCreated || 0 : totalCreatedVolume || 0}
                                    displayType={'text'}
                                    thousandSeparator={true}
                                />
                            </div>
                            <div className="LoadSummary-Detail-Item totals-item justify-content-start">
                                <NumberFormat
                                    value={selectedTab === 'Loads' ? totalInProgress || 0 : totalInProgressVolume || 0}
                                    displayType={'text'}
                                    thousandSeparator={true}
                                />
                            </div>
                            <div className="LoadSummary-Detail-Item totals-item justify-content-start">
                                <NumberFormat
                                    value={selectedTab === 'Loads' ? totalDelivered || 0 : totalDeliveredVolume || 0}
                                    displayType={'text'}
                                    thousandSeparator={true}
                                />
                            </div>
                            <div className="LoadSummary-Detail-Item totals-item justify-content-start">
                                <NumberFormat
                                    value={selectedTab === 'Loads' ? totalRemaining || 0 : totalRemainingVolume || 0}
                                    displayType={'text'}
                                    thousandSeparator={true}
                                />
                            </div>
                            <div className="LoadSummary-Detail-Item totals-item justify-content-start"></div>
                        </div>
                    )}
                </div>
            ) : (
                <div />
            );
        });

    return (
        <div className="LoadSummary-Container">
            {isDisplayLoadsEnabled && (
                <div>
                    <div className="LoadSummary-Link-Container Row justify-content-start">
                        <div
                            className={`LoadSummary-Tab ${selectedTab === 'Loads' && 'LoadSummary-TabSelected'}`}
                            onClick={(): void => {
                                setSelectedTab('Loads');
                            }}
                        >
                            Loads
                        </div>
                        <div
                            className={`LoadSummary-Tab LoadSummary-TabWithMargin ${selectedTab === 'Volume' &&
                                'LoadSummary-TabSelected'}`}
                            onClick={(): void => {
                                setSelectedTab('Volume');
                            }}
                        >
                            Volume
                        </div>
                    </div>
                    <div className="Row header-row">
                        <div className="LoadSummary-Header-Item">Proppant</div>
                        <div className="LoadSummary-Header-Item">
                            Active
                            {activeLoading && <Loading />}
                        </div>
                        <div className="LoadSummary-Header-Item"></div>
                        <div className="LoadSummary-Header-Item LoadSummary-Name">
                            Loading Facility
                        </div>
                        <div className="LoadSummary-Header-Item">Sand PO</div>
                        <div className="LoadSummary-Header-Item">Transportation PO</div>
                        <div className="LoadSummary-Header-Item">Created</div>
                        <div className="LoadSummary-Header-Item">InProgress</div>
                        <div className="LoadSummary-Header-Item">Delivered</div>
                        <div className="LoadSummary-Header-Item">Remaining</div>
                        <div className="LoadSummary-Header-Item"></div>
                    </div>
                    <div>{loadSummaryComponents}</div>{' '}
                </div>
            )}
            <div>
                {showErrorModal && (
                    <ErrorActiveModal
                        show={showErrorModal}
                        onClickHide={handleOnCloseErrorModal}
                        textMessage={errorMsg}
                    />
                )}
            </div>
            <div>
                {showSuccessModal && (
                    <ErrorActiveModal
                        show={showSuccessModal}
                        onClickHide={handleOnCloseSuccessModal}
                        textMessage={successMsg}
                    />
                )}
            </div>
        </div>
    );
};

export default LoadSummary;

